import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Input } from 'src/@vymo/ui/atoms';
import TagInput from 'src/@vymo/ui/atoms/tagInput';
import { ReactComponent as CopyIconSvg } from 'src/assets/icons/copy.svg';
import { getClientConfigData } from 'src/workspace/utils';
import { useFormContext } from '../../formProvider';
import { FormContextProps } from '../../formProvider/types';
import { referralOnclick } from '../referralDropdown/queries';
import HierarchyModal from './hierarchyModal';
import { getCachedEntities } from './queries';
import type { EntityWithHierarchySelectionProps } from './types';

const emptyHierarchyValue = {
  values: '[]',
  include_descendants: false,
};

function EntityWithHierarchySelection({
  code,
  hint,
  value = emptyHierarchyValue,
  onChange,
  source,
  setReferralData,
  viewMode = false,
  version,
  multi,
  includeDescendantsHint = '',
  ...props
}: EntityWithHierarchySelectionProps &
  FormContextProps & { version: FormContextProps['config']['version'] }) {
  const [showModal, setShowModal] = useState(false);
  const [selectedEntities, setSelectedEntities] = useState<
    Array<{ code: string; name: string }>
  >([]);
  const { getAllUsers, getHierarchy } = useFormContext(false);

  const hierarchyList = useMemo(
    () => getCachedEntities(source, getAllUsers(), getHierarchy()),
    [getAllUsers, getHierarchy, source],
  );

  let hierarchyValueOptions;

  try {
    hierarchyValueOptions = JSON.parse(value?.values);
  } catch (error) {
    throw new Error(
      'Invalid hierarchy value. Value should be stringified json',
    );
  }

  // useMemo(() => {
  //   const hierarchyCode = hierarchyValueOptions?.[0]?.code;
  //   if (hierarchyList?.length > 0 && hierarchyCode) {
  //     const inputsMap = hierarchyList?.find(
  //       (option: any) => option.id === hierarchyCode,
  //     );
  //     setReferralData({ fieldCode: code, data: inputsMap });
  //   }
  // }, [code, hierarchyValueOptions, hierarchyList, setReferralData]);

  useEffect(() => {
    if (hierarchyValueOptions) {
      setSelectedEntities(hierarchyValueOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(hierarchyValueOptions)]);

  const onClickInput = () => {
    setShowModal(true);
  };

  const onTagInputChange = (inputValues) => {
    const updatedValues = inputValues?.map((inputValue) => ({
      code: inputValue?.value,
      name: inputValue?.label,
    }));

    setSelectedEntities(updatedValues);
    onChange(
      {
        ...(value ?? {}),
        values: JSON.stringify(updatedValues),
      },
      {} as React.ChangeEvent<any>,
    );
  };

  return !viewMode ? (
    <>
      {multi ? (
        <TagInput
          value={hierarchyValueOptions?.map((valueOption) => ({
            label: valueOption?.name,
            value: valueOption?.code,
          }))}
          onClick={onClickInput}
          onChange={onTagInputChange}
          {..._.omit(props, ['label'])}
          iconRight={<CopyIconSvg />}
        />
      ) : (
        <Input
          value={hierarchyValueOptions?.[0]?.name}
          onClick={onClickInput}
          onChange={(inputValue) => {
            if (inputValue === '') {
              setSelectedEntities([]);
              onChange(
                { ...(value ?? {}), values: '[]' },
                {} as React.ChangeEvent<any>,
              );
            }
          }}
          {..._.omit(props, ['label'])}
          iconRight={<CopyIconSvg />}
        />
      )}
      <Checkbox
        size="small"
        disabled={!hierarchyValueOptions?.length}
        value={Boolean(value?.include_descendants)}
        label={includeDescendantsHint}
        onChange={(checked) => {
          onChange(
            {
              ...(value || {}),
              include_descendants: checked,
            },
            {} as React.ChangeEvent<any>,
          );
        }}
      />
      {showModal && (
        <HierarchyModal
          hint={hint}
          source={source}
          multi={multi}
          value={value}
          selectedEntities={selectedEntities}
          setSelectedEntities={setSelectedEntities}
          onChange={onChange}
          setShowModal={setShowModal}
          hierarchyList={hierarchyList}
        />
      )}
    </>
  ) : (
    <Button
      variant="subtle"
      type="primary"
      size="small"
      onClick={() =>
        referralOnclick(
          version,
          source,
          hierarchyValueOptions?.[0]?.code,
          getClientConfigData(),
        )
      }
    >
      {hierarchyValueOptions?.[0]?.name}
    </Button>
  );
}

export default EntityWithHierarchySelection;
